import { useEffect, useMemo, useState } from "react";
import PaymentService from "../../app/data/payment/paymentService";
import { PublicPaymentGroup } from "../../app/data/payment/PaymentSession";
import { ProbillEntry } from "../shipments/labels-receipts/types";

const paymentService = PaymentService.getInstance();

export const PUBLIC_PAY_MAX_INVOICES = 25;

const lookupKeyFor = (entries: ProbillEntry[]): string =>
  entries
    .filter((entry) => entry.value != null && (entry.status === "valid" || entry.status === "validating"))
    .map((entry) => `${entry.id}:${entry.value}:${entry.status}`)
    .join("|");

export const usePayableProbills = () => {
  const [entries, setEntries] = useState<ProbillEntry[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [lookupGroups, setLookupGroups] = useState<PublicPaymentGroup[]>([]);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const lookupKey = lookupKeyFor(entries);

  useEffect(() => {
    const pending = entries.filter((entry) => entry.status === "validating" && entry.value != null);
    if (!pending.length) {
      return;
    }
    const numbers = entries
      .filter((entry) => entry.value != null && (entry.status === "valid" || entry.status === "validating"))
      .map((entry) => entry.value as number);
    const pendingIds = new Set(pending.map((entry) => entry.id));
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      paymentService.lookupPublicProbills(numbers).then((response) => {
        if (cancelled) {
          return;
        }
        if (!response.ok() || !response.data) {
          const err = response.getError?.();
          setLookupError(typeof err === "string" ? err : "Unable to look up those XGS probill numbers.");
          setEntries((current) =>
            current.map((entry) =>
              pendingIds.has(entry.id)
                ? { ...entry, status: "invalid", errorMessage: "Unable to look up" }
                : entry
            )
          );
          return;
        }
        setLookupError(null);
        const openByNumber = new Map(
          (response.data.probills || []).map((line) => [line.probillNumber, line.openAmountCents])
        );
        setEntries((current) =>
          current.map((entry) => {
            if (!pendingIds.has(entry.id) || entry.value == null) {
              return entry;
            }
            const cents = openByNumber.get(entry.value) ?? 0;
            if (cents <= 0) {
              return { ...entry, status: "invalid", errorMessage: "No open amount" };
            }
            return { ...entry, status: "valid" };
          })
        );
        setLookupGroups(response.data.groups || []);
      });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // lookupKey changes only when the payable chip set changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  const groups = useMemo(() => {
    const validValues = new Set(
      entries.filter((entry) => entry.status === "valid" && entry.value != null).map((entry) => entry.value)
    );
    return lookupGroups
      .map((group) => {
        const probills = (group.probills || []).filter((line) => validValues.has(line.probillNumber));
        const openAmountCents = probills.reduce((sum, line) => sum + line.openAmountCents, 0);
        return { ...group, probills, openAmountCents };
      })
      .filter((group) => group.probills.length > 0)
      .map((group, index) => ({ ...group, groupNumber: index + 1 }));
  }, [entries, lookupGroups]);

  const validating = entries.some((entry) => entry.status === "validating");

  return {
    entries,
    setEntries,
    inputValue,
    setInputValue,
    groups,
    lookupError,
    validating,
  };
};
